import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import apiRequest from 'lib/axios/api-request'
import {
  ProbateEstatePerson,
  ProbateEstatePeopleAttributes,
} from 'lib/models/probate-estate'
import { PRODUCTS } from 'utils/enums'
import type { KycCheckRun } from 'lib/models/kyc-check-run'

type SyncProbateEstatePersonWithCallerParams = {
  contactId: number
  attributes: Partial<ProbateEstatePeopleAttributes>
  isPrimaryExecutor?: boolean
  isPrimaryAdministrator?: boolean
}

type UpdatePersonSignatureRequestParams = {
  personId: number
  sent: boolean
  signed: boolean
  documentName: string
}

type SendDocusignSignatureRequestParams = {
  personId: number
  documentName: string
}

interface PeopleContextProps {
  people: ProbateEstatePerson[]
  createPerson: (
    attributes: Partial<ProbateEstatePeopleAttributes>
  ) => Promise<void>
  createPersonKycCheck: (personId: number) => Promise<unknown>
  listPersonKycChecks: (personId: number) => Promise<KycCheckRun[]>
  updatePerson: (person: Partial<ProbateEstatePerson>) => Promise<void>
  updatePersonSignatureRequest: (
    params: UpdatePersonSignatureRequestParams
  ) => Promise<void>
  sendDocusignSignatureRequest: (
    params: SendDocusignSignatureRequestParams
  ) => Promise<ProbateEstatePerson>
  deletePerson: (id: number) => Promise<void>
  bulkDeletePeople: (personalRepresentativeType: string) => Promise<void>
  syncProbateEstatePersonWithCaller: (
    params: SyncProbateEstatePersonWithCallerParams
  ) => Promise<void>
}

const PeopleContext = createContext<PeopleContextProps>({
  people: [],
  createPerson: (attributes: Partial<ProbateEstatePeopleAttributes>) =>
    Promise.resolve(),
  createPersonKycCheck: (personId: number) => Promise.resolve(),
  listPersonKycChecks: (personId: number) => Promise.resolve([]),
  updatePerson: (person: Partial<ProbateEstatePerson>) => Promise.resolve(),
  updatePersonSignatureRequest: (params: UpdatePersonSignatureRequestParams) =>
    Promise.resolve(),
  sendDocusignSignatureRequest: () =>
    Promise.resolve({} as ProbateEstatePerson),
  deletePerson: (id: number) => Promise.resolve(),
  bulkDeletePeople: (personalRepresentativeType: string) => Promise.resolve(),
  syncProbateEstatePersonWithCaller: (
    params: SyncProbateEstatePersonWithCallerParams
  ) => Promise.resolve(),
})

const PeopleProvider = ({
  children,
  probateEstateId,
}: PropsWithChildren<{
  probateEstateId: number
}>) => {
  const [people, setPeople] = useState<ProbateEstatePerson[]>([])
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    if (!probateEstateId) return

    apiRequest({
      url: `/api/probate-estates/${probateEstateId}/people`,
      method: 'GET',
    }).then((response) => {
      setPeople(response.data.data)
      setLoaded(true)
    })
  }, [probateEstateId])

  const createPerson = async (
    attributes: Partial<ProbateEstatePeopleAttributes>
  ) => {
    const apiResponse = await apiRequest({
      url: `/api/probate-estates/${probateEstateId}/people`,
      method: 'POST',
      data: {
        data: {
          type: 'probate_estate_people',
          attributes: { ...attributes },
        },
      },
    })
    setPeople([...people, apiResponse.data.data])
  }

  const createPersonKycCheck = async (personId: number) => {
    return apiRequest({
      url: `/api/kyc-checks`,
      method: 'POST',
      data: {
        product: PRODUCTS.PROBATE,
        personId,
      },
    })
  }

  const listPersonKycChecks = async (
    personId: number
  ): Promise<KycCheckRun[]> => {
    const { data } = await apiRequest({
      url: `/api/kyc-checks?product=${PRODUCTS.PROBATE}&personId=${personId}`,
      method: 'GET',
    })
    return data.data
  }

  const updatePerson = async (person: Partial<ProbateEstatePerson>) => {
    const apiResponse = await apiRequest({
      url: `/api/probate-estates/${probateEstateId}/people/${person.id}`,
      method: 'PATCH',
      data: {
        data: {
          type: 'probate_estate_people',
          id: person.id,
          attributes: person.attributes,
        },
      },
    })

    const updatedPersonData: ProbateEstatePerson = apiResponse.data.data
    const updatedPeople = people.map((peopleItem) =>
      peopleItem.id === updatedPersonData.id ? updatedPersonData : peopleItem
    )

    setPeople(updatedPeople)
  }

  const updatePersonSignatureRequest = async ({
    personId,
    sent,
    signed,
    documentName,
  }: UpdatePersonSignatureRequestParams) => {
    const apiResponse = await apiRequest({
      url: '/api/signature-requests/dignity/probate',
      method: 'POST',
      data: {
        provider: 'manual',
        personId,
        documentName,
        sent,
        signed,
      },
    })

    const updatedPersonData: ProbateEstatePerson = apiResponse.data.data
    const updatedPeople = people.map((peopleItem) =>
      peopleItem.id === updatedPersonData.id ? updatedPersonData : peopleItem
    )

    setPeople(updatedPeople)

    return
  }

  const sendDocusignSignatureRequest = async ({
    personId,
    documentName,
  }: SendDocusignSignatureRequestParams): Promise<ProbateEstatePerson> => {
    const apiResponse = await apiRequest({
      url: '/api/signature-requests/dignity/probate',
      method: 'POST',
      data: {
        provider: 'docusign',
        personId,
        documentName,
      },
    })

    const updatedPersonData: ProbateEstatePerson = apiResponse.data.data
    const updatedPeople = people.map((peopleItem) =>
      peopleItem.id === updatedPersonData.id ? updatedPersonData : peopleItem
    )

    setPeople(updatedPeople)

    return updatedPersonData
  }

  const deletePerson = async (id: number) => {
    await apiRequest({
      url: `/api/probate-estates/${probateEstateId}/people/${id}`,
      method: 'DELETE',
    }).then(() => {
      const updatedPeople = people.filter((p) => p.id !== id)
      setPeople(updatedPeople)
    })
  }

  const bulkDeletePeople = async (personalRepresentativeType: string) => {
    await apiRequest({
      url: `/api/probate-estates/${probateEstateId}/people/bulk-delete`,
      method: 'POST',
      data: {
        data: {
          type: 'probate_estate_people',
          attributes: { personalRepresentativeType },
        },
      },
    }).then(() => {
      const updatedPeople = people.filter(
        (p) =>
          p.attributes.personalRepresentativeType !== personalRepresentativeType
      )
      setPeople(updatedPeople)
    })
  }

  const syncProbateEstatePersonWithCaller = async ({
    contactId,
    attributes,
    isPrimaryExecutor,
    isPrimaryAdministrator,
  }: {
    contactId: number
    attributes: Partial<ProbateEstatePeopleAttributes>
    isPrimaryExecutor?: boolean
    isPrimaryAdministrator?: boolean
  }) => {
    if (!loaded) return

    const isPrimary = isPrimaryExecutor || isPrimaryAdministrator

    // Build the attributes for the person
    const personAttributes: Partial<ProbateEstatePeopleAttributes> = {
      ...attributes,
      personalRepresentativeType: isPrimaryExecutor
        ? 'executor'
        : isPrimaryAdministrator
          ? 'administrator_without_will'
          : undefined,
      isCustomer: !!isPrimary,
    }

    // Find the person for this contact
    const person = people.find(
      (p) => p.attributes.originalContactId === contactId
    )
    if (isPrimary) {
      // If caller is now primary, remove
      const previousPrimary = people.find(
        (p) => p.attributes.isCustomer && !p.attributes.originalContactId
      )
      if (previousPrimary) {
        await updatePerson({
          id: previousPrimary.id,
          attributes: { isCustomer: false },
        })
      }

      if (person) {
        await updatePerson({
          id: person.id,
          attributes: personAttributes,
        })
      } else {
        await createPerson(personAttributes)
      }
    } else if (person) {
      // If caller is no longer executor or administrator, remove from the probate estate
      await deletePerson(person.id)
    }
  }

  return (
    <PeopleContext.Provider
      value={{
        people,
        createPerson,
        createPersonKycCheck,
        listPersonKycChecks,
        updatePerson,
        updatePersonSignatureRequest,
        sendDocusignSignatureRequest,
        deletePerson,
        bulkDeletePeople,
        syncProbateEstatePersonWithCaller,
      }}
    >
      {children}
    </PeopleContext.Provider>
  )
}

const usePeople = () => useContext(PeopleContext)

export { PeopleProvider, usePeople }
